import { useContext, useEffect, useMemo, useState } from 'react';

import { Assessment, Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    ThemeProvider,
    Tooltip,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';

import * as UnidadeCondominialPessoaAPI from '../../../api/unidadeCondominialPessoa';
import { Context } from '../../../context/UserContext';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import { useMRTState } from '../../../hooks/useMRTState';
import { podeCancelar, podeEditar, podeImprimir, podeIncluir } from '../../../hooks/usePermisionMRT';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import { obterPermissoesUsuario } from '../../../utils/funcoes';
import tableThemeCustom from '../../form/tableThemeCustom';

function formatarData(value) {
    if (!value) return '';

    const [ano, mes, dia] = String(value).slice(0, 10).split('-');

    return ano && mes && dia ? `${dia}/${mes}/${ano}` : '';
}

function formatarDataHora(value) {
    if (!value) return '';

    const data = new Date(value);

    return Number.isNaN(data.getTime()) ? '' : data.toLocaleString('pt-BR');
}

function simNao(value) {
    return value ? 'Sim' : 'Não';
}

function Index() {
    const { parametro } = useContext(Context);
    const { pessoaId, empresaId } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    const urlParams = new URLSearchParams(window.location.search);
    const ul = urlParams.get('ul');
    const li = urlParams.get('li');

    const [token] = useState(localStorage.getItem('token') || '');
    const [dados, setDados] = useState([]);
    const [vinculoExcluir, setVinculoExcluir] = useState(null);
    const [excluindo, setExcluindo] = useState(false);
    const [dialogCenso, setDialogCenso] = useState(false);
    const [gerandoCenso, setGerandoCenso] = useState(false);
    const [periodoCenso, setPeriodoCenso] = useState({
        anoInicio: 2017,
        anoFim: new Date().getFullYear(),
    });

    const mrt = useMRTState({
        storageKey: 'unidade-condominial-pessoa.index',
        initialState: {
            columnFilterFns: {},
            columnFilters: [],
            globalFilter: '',
            sorting: [],
            pagination: {
                pageIndex: 0,
                pageSize: parametro?.paginacao || 10,
            },
        },
    });

    const {
        columnFilterFns,
        columnFilters,
        globalFilter,
        sorting,
        pagination,
        setIsLoading,
        setIsError,
        setIsRefetching,
        setRowCount,
    } = mrt;

    const mrtDefault = getMRTDefaultProps({ mrt, parametro });

    async function fetchData() {
        dados.length ? setIsRefetching(true) : setIsLoading(true);

        try {
            const response = await UnidadeCondominialPessoaAPI.listar({
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: JSON.stringify(columnFilters),
                filtersMode: JSON.stringify(columnFilterFns),
                globalFilter: globalFilter || '',
                sorting: JSON.stringify(sorting),
            });

            setDados(response.data.data || []);
            setRowCount(Number(response.data.meta?.totalRowCount) || 0);
            setIsError(false);
        } catch (error) {
            setIsError(true);
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar as pessoas das unidades condominiais.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }

    useEffect(() => {
        if (!token) {
            navigate('/Auth', { replace: true });
            return;
        }

        fetchData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, columnFilterFns, columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting]);

    useEffect(() => {
        if (!pessoaId || !empresaId || !ul || !li) return;

        obterPermissoesUsuario(pessoaId, empresaId, ul, li);
    }, [pessoaId, empresaId, ul, li]);

    function novoVinculo() {
        const params = new URLSearchParams(urlParams);

        params.delete('uuid');
        params.set('btnText', 'Cadastrar');

        navigate('/UnidadeCondominialPessoas/CreateOrEdit', params);
    }

    function editarVinculo(vinculo) {
        const params = new URLSearchParams(urlParams);

        params.set('uuid', vinculo.uuid);
        params.set('btnText', 'Atualizar');

        navigate('/UnidadeCondominialPessoas/CreateOrEdit', params);
    }

    async function confirmarExclusao() {
        if (!vinculoExcluir?.uuid || excluindo) return;

        setExcluindo(true);

        try {
            const response = await UnidadeCondominialPessoaAPI.excluir(vinculoExcluir.uuid);

            setVinculoExcluir(null);
            setFlashMessage(response.data.message, response.data.type);
            await fetchData();
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível excluir o vínculo entre a pessoa e a unidade.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setExcluindo(false);
        }
    }

    async function gerarCenso() {
        if (gerandoCenso) return;

        const anoInicio = Number(periodoCenso.anoInicio);
        const anoFim = Number(periodoCenso.anoFim);
        const anoAtual = new Date().getFullYear();

        if (!Number.isInteger(anoInicio) || anoInicio < 1900 || anoInicio > anoAtual) {
            setFlashMessage(`O ano inicial deve estar entre 1900 e ${anoAtual}.`, 'warning');
            return;
        }

        if (!Number.isInteger(anoFim) || anoFim < anoInicio || anoFim > anoAtual) {
            setFlashMessage(`O ano final deve estar entre ${anoInicio} e ${anoAtual}.`, 'warning');
            return;
        }

        const janela = window.open('', '_blank');

        if (!janela) {
            setFlashMessage('O navegador bloqueou a abertura do relatório. Permita pop-ups para este sistema.', 'warning');
            return;
        }

        setGerandoCenso(true);

        try {
            janela.document.title = 'Gerando censo populacional...';
            janela.document.body.innerHTML = '<p style="font-family: sans-serif">Gerando censo populacional...</p>';

            const response = await UnidadeCondominialPessoaAPI.censo({
                empresaId,
                anoInicio,
                anoFim,
            });
            const arquivo = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(arquivo);

            janela.location.href = url;
            window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
            setDialogCenso(false);
        } catch (error) {
            janela.close();
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível gerar o censo populacional.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setGerandoCenso(false);
        }
    }

    const columns = useMemo(
        () => [
            {
                id: 'unidadeCondominialPessoas',
                header: 'PESSOAS POR UNIDADE CONDOMINIAL',
                columns: [
                    { accessorKey: 'Empresa.nomeFantasia', header: 'Empresa' },
                    { accessorKey: 'UnidadeCondominial.codigo', header: 'Unidade' },
                    { accessorKey: 'UnidadeCondominial.descricao', header: 'Descrição da unidade' },
                    { accessorKey: 'Pessoa.descricao', header: 'Pessoa' },
                    { accessorKey: 'Pessoa.CPForCNPJ', header: 'CPF/CNPJ' },
                    {
                        accessorKey: 'proprietario',
                        header: 'Proprietário',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'responsavelFinanceiro',
                        header: 'Resp. financeiro',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'morador',
                        header: 'Morador',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'inquilino',
                        header: 'Inquilino',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'dataInicio',
                        header: 'Início',
                        Cell: ({ cell }) => formatarData(cell.getValue()),
                    },
                    {
                        accessorKey: 'dataFim',
                        header: 'Término',
                        Cell: ({ cell }) => formatarData(cell.getValue()),
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'updatedAt',
                        header: 'Atualizado em',
                        Cell: ({ cell }) => formatarDataHora(cell.getValue()),
                    },
                ],
            },
        ],
        []
    );

    const theme = tableThemeCustom();

    return (
        <div>
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={dados}
                    getRowId={(row) => row.uuid}
                    renderTopToolbarCustomActions={() => (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button color="secondary" variant="contained" disabled={!podeIncluir()} onClick={novoVinculo}>
                                Novo
                            </Button>

                            <Button
                                color="secondary"
                                variant="outlined"
                                startIcon={<Assessment />}
                                disabled={!podeImprimir()}
                                onClick={() => setDialogCenso(true)}
                            >
                                Censo populacional
                            </Button>
                        </Box>
                    )}
                    renderRowActions={({ row }) => (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Editar vínculo entre pessoa e unidade">
                                <span>
                                    <IconButton
                                        color="primary"
                                        disabled={!podeEditar()}
                                        onClick={() => editarVinculo(row.original)}
                                    >
                                        <Edit />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Excluir vínculo entre pessoa e unidade">
                                <span>
                                    <IconButton
                                        color="error"
                                        disabled={!podeCancelar()}
                                        onClick={() => setVinculoExcluir(row.original)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                />
            </ThemeProvider>

            <Dialog
                open={Boolean(vinculoExcluir)}
                onClose={() => {
                    if (!excluindo) setVinculoExcluir(null);
                }}
            >
                <DialogTitle>Excluir vínculo entre pessoa e unidade?</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        O vínculo de <strong>{vinculoExcluir?.Pessoa?.descricao}</strong> com a unidade{' '}
                        <strong>{vinculoExcluir?.UnidadeCondominial?.descricao}</strong> será excluído.
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button disabled={excluindo} onClick={() => setVinculoExcluir(null)}>
                        Cancelar
                    </Button>

                    <Button variant="contained" color="error" disabled={excluindo} onClick={confirmarExclusao}>
                        {excluindo ? 'Excluindo...' : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogCenso} onClose={() => !gerandoCenso && setDialogCenso(false)}>
                <DialogTitle>Gerar censo populacional</DialogTitle>

                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Uma pessoa será contada uma única vez em cada ano no qual tenha residido ao menos um dia.
                    </DialogContentText>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Ano inicial"
                            type="number"
                            value={periodoCenso.anoInicio}
                            onChange={(event) => setPeriodoCenso((anterior) => ({ ...anterior, anoInicio: event.target.value }))}
                            inputProps={{ min: 1900, max: new Date().getFullYear() }}
                        />

                        <TextField
                            label="Ano final"
                            type="number"
                            value={periodoCenso.anoFim}
                            onChange={(event) => setPeriodoCenso((anterior) => ({ ...anterior, anoFim: event.target.value }))}
                            inputProps={{ min: 1900, max: new Date().getFullYear() }}
                        />
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button disabled={gerandoCenso} onClick={() => setDialogCenso(false)}>
                        Cancelar
                    </Button>

                    <Button variant="contained" color="secondary" disabled={gerandoCenso} onClick={gerarCenso}>
                        {gerandoCenso ? 'Gerando...' : 'Gerar PDF'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Index;
